import React, { useCallback, useRef, useState } from 'react';
import { GridCellModes, GridCellModesModel, GridCellParams } from '@mui/x-data-grid-premium';

export const useSingleClickEditing = () => {
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  // Flag to prevent onCellModesModelChange from overriding the Edit mode
  // set by handleCellClick during the same click event.
  const cellClickRef = useRef(false);

  const handleCellClick = useCallback((params: GridCellParams, event: React.MouseEvent) => {
    // Ignore portal
    if (!event.currentTarget.contains(event.target as Element)) {
      return;
    }

    // In MUI X v7, the grid fires onCellModesModelChange during the same
    // click event with the clicked cell set to View (default double-click
    // behavior), which would undo the single-click Edit we set below.
    cellClickRef.current = true;
    setTimeout(() => {
      cellClickRef.current = false;
    }, 0);

    setCellModesModel((prevModel) => {
      // In MUI X v7, cells omitted from the model keep their current mode,
      // so we must explicitly revert all tracked cells to View.
      const allView: GridCellModesModel = {};
      for (const id in prevModel) {
        const fields: GridCellModesModel[string] = {};
        for (const field in prevModel[id]) {
          fields[field] = { mode: GridCellModes.View };
        }
        allView[id] = fields;
      }

      if (!params.isEditable) {
        return allView;
      }

      return {
        ...allView,
        [params.id]: {
          ...allView[params.id],
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback((newModel: GridCellModesModel) => {
    // Skip model changes triggered by the same click event that
    // handleCellClick already handled — prevents MUI's default
    // double-click behavior from reverting the cell back to View.
    if (cellClickRef.current) {
      cellClickRef.current = false;
      return;
    }
    setCellModesModel(newModel);
  }, []);

  return { handleCellClick, handleCellModesModelChange, cellModesModel };
};
